import { createElement } from '@syncfusion/ej2-base';
import * as constants from '../../../common/constant';
import { decoupleReference, getBlockContentElement, getBlockModelById, getDataCell, getSelectedCells, setCursorPosition, setTableWidthMode, toDomCol, toModelRow } from '../../../common/utils/index';
export class TableUIManager {
    constructor(parent) {
        // State
        this.hoveredRow = null;
        this.hoveredColIndex = null;
        this.isMultiSelecting = false;
        this.isResizing = false;
        this.lastRowAnchorIndex = null;
        this.lastColAnchorIndex = null;
        this.parent = parent;
    }
    init(table, blockElement, blockModel) {
        this.table = table;
        this.tableContainer = table.parentElement;
        this.blockElement = blockElement;
        this.blockModel = blockModel;
        this.blockId = table.getAttribute('data-block-id');
        this.createUiElements();
        this.mountUiElements();
        this.wireDelegatedMousemove();
        this.wireRowInsert();
        this.wireActionHandles();
        this.wireColInsert();
        this.wireColResize();
        this.wireFocusAndCleanup();
        this.wireObservers();
    }
    // 1) Create all UI elements
    createUiElements() {
        this.rowInsertHandle = createElement('div', { className: 'e-row-insert-handle e-icons e-plus', attrs: { contenteditable: 'false' }, styles: 'display: none' });
        this.rowActionHandle = createElement('div', { className: 'e-row-action-handle e-action-handle', attrs: { contenteditable: 'false', 'data-block-id': this.blockId, 'data-icon-type': 'row' }, styles: 'display: none' });
        const rowActionHandleIcon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        this.rowActionHandle.appendChild(rowActionHandleIcon);
        this.rowHoverLine = createElement('div', { className: 'e-row-hover-line', styles: 'display: none', attrs: { contenteditable: 'false' } });
        this.rowTopDot = createElement('div', { className: 'e-row-dot', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.rowBottomDot = createElement('div', { className: 'e-row-dot', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.colInsertHandle = createElement('div', { className: 'e-col-insert-handle e-icons e-plus', attrs: { contenteditable: 'false' }, styles: 'display: none' });
        this.colActionHandle = createElement('div', { className: 'e-col-action-handle e-action-handle', attrs: { contenteditable: 'false', 'data-block-id': this.blockId, 'data-icon-type': 'col' }, styles: 'display: none' });
        const colActionHandleIcon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        colActionHandleIcon.style.transform = 'rotate(90deg)';
        this.colActionHandle.appendChild(colActionHandleIcon);
        this.colHoverLine = createElement('div', { className: 'e-col-hover-line', styles: 'display: none', attrs: { contenteditable: 'false' } });
        this.colLeftDot = createElement('div', { className: 'e-col-dot', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.colRightDot = createElement('div', { className: 'e-col-dot', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.rowPinned = createElement('div', { className: 'e-row-action-handle e-pinned e-action-handle', attrs: { contenteditable: 'false', 'data-icon-type': 'row' }, styles: 'display: none' });
        const rowPinnedHandleIcon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        this.rowPinned.appendChild(rowPinnedHandleIcon);
        this.colPinned = createElement('div', { className: 'e-col-action-handle e-pinned e-action-handle', attrs: { contenteditable: 'false', 'data-icon-type': 'col' }, styles: 'display: none' });
        const colPinnedHandleIcon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        colPinnedHandleIcon.style.transform = 'rotate(90deg)';
        this.colPinned.appendChild(colPinnedHandleIcon);
        this.rowTopHit = createElement('div', { className: 'e-row-dot-hit', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.rowBottomHit = createElement('div', { className: 'e-row-dot-hit', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.colLeftHit = createElement('div', { className: 'e-col-dot-hit', styles: 'display:none', attrs: { contenteditable: 'false' } });
        this.colRightHit = createElement('div', { className: 'e-col-dot-hit', styles: 'display:none', attrs: { contenteditable: 'false' } });
        // full-height column-resize handle (invisible until needed)
        this.colResizeHandle = createElement('div', {
            className: 'e-col-resize-handle',
            attrs: { contenteditable: 'false' },
            styles: 'display:none; position:absolute; pointer-events:auto;'
        });
    }
    // 2) Mount elements to block element in the same order
    mountUiElements() {
        [this.rowInsertHandle, this.rowHoverLine, this.rowActionHandle, this.rowTopDot, this.rowBottomDot, this.colInsertHandle,
            this.colHoverLine, this.colActionHandle, this.colLeftDot, this.colRightDot, this.rowPinned, this.colPinned]
            .forEach((el) => this.blockElement.appendChild(el));
        [this.rowTopHit, this.rowBottomHit, this.colLeftHit, this.colRightHit]
            .forEach((el) => this.blockElement.appendChild(el));
        // mount the resize handle on top
        this.blockElement.appendChild(this.colResizeHandle);
    }
    // 3) Delegated mouse move handling (exact logic preserved)
    wireDelegatedMousemove() {
        this.table.addEventListener('mousemove', (e) => {
            const target = e.target;
            const isRowNumberCell = target.closest('td.e-row-number, th.e-row-number') !== null;
            const isPlainHeaderCell = target.closest('th.e-row-number') !== null;
            const hasBodyRows = this.table.tBodies[0].rows.length > 0 ? true : false;
            if (this.isMultiSelecting || this.isResizing || !target.closest('td, th')) {
                this.hideRowUI();
                this.hideColUI();
                this.hideHitZones();
                return;
            }
            // Hide row UI only for plain header cell when body rows exist
            if (isPlainHeaderCell && hasBodyRows) {
                this.hideRowUI();
                this.hideColUI();
                this.hideHitZones();
                return;
            }
            // hide the resize handle by default; we'll re-show if near a column boundary
            this.colResizeHandle.style.display = 'none';
            const xLeft = this.table.parentElement.offsetLeft - 8;
            // Row positioning (dots only)
            const row = target.closest('tr');
            this.hoveredRow = row;
            const rowRect = row.getBoundingClientRect();
            const blockRect = this.blockElement.getBoundingClientRect();
            this.rowActionHandle.dataset.rowIndex = Array.from(this.table.rows).indexOf(row).toString();
            this.rowActionHandle.style.top = rowRect.top - blockRect.top + 'px';
            this.rowActionHandle.style.height = `${rowRect.height}` + 'px';
            this.rowActionHandle.style.display = target.tagName === 'TH' ? 'none' : 'flex';
            const isHeaderRow = this.hoveredRow && this.hoveredRow.querySelector('th') !== null;
            let showTopDot = true;
            let showBottomDot = true;
            if (isHeaderRow) {
                if (hasBodyRows) {
                    // Normal case: header with body rows → hide both dots
                    showTopDot = false;
                    showBottomDot = false;
                }
                else {
                    // Only header exists → show ONLY bottom dot
                    showTopDot = false;
                    showBottomDot = true;
                }
            }
            else {
                // Body row → show both dots normally
                showTopDot = true;
                showBottomDot = true;
            }
            if (showTopDot || showBottomDot) {
                // Show row dots at left side (top and bottom of row)
                this.rowTopDot.style.left = `${xLeft - 4}` + 'px';
                this.rowBottomDot.style.left = `${xLeft - 4}` + 'px';
                this.rowTopDot.style.top = `${rowRect.top - blockRect.top - 3}` + 'px';
                this.rowBottomDot.style.top = `${rowRect.bottom - blockRect.top - 3}` + 'px';
                this.rowTopDot.style.display = showTopDot ? 'block' : 'none';
                this.rowBottomDot.style.display = showBottomDot ? 'block' : 'none';
            }
            else {
                this.rowTopDot.style.display = 'none';
                this.rowBottomDot.style.display = 'none';
            }
            // Hit zones at left side for dot
            const xLeftHit = xLeft - 12;
            this.rowTopHit.style.left = `${xLeftHit}` + 'px';
            this.rowBottomHit.style.left = `${xLeftHit}` + 'px';
            this.rowTopHit.style.top = `${rowRect.top - blockRect.top - 15}` + 'px';
            this.rowBottomHit.style.top = `${rowRect.bottom - blockRect.top - 15}` + 'px';
            this.rowTopHit.style.display = 'block';
            this.rowBottomHit.style.display = 'block';
            // Column positioning (dots only)
            const cell = target.closest('td, th');
            const cellRect = cell.getBoundingClientRect();
            const rowEl = cell.parentElement;
            const colIndex = Array.from(rowEl.cells).filter((c) => !c.classList.contains('e-row-number')).indexOf(cell);
            this.hoveredColIndex = colIndex;
            const dotY = this.table.parentElement.offsetTop - 8;
            const dotHalfWidth = 3.2;
            const borderValue = Math.round(parseFloat(getComputedStyle(cell).borderWidth));
            const leftColX = cellRect.left - blockRect.left - borderValue - dotHalfWidth;
            const rightColX = cellRect.right - blockRect.left - borderValue - dotHalfWidth;
            this.colLeftDot.style.left = `${leftColX}` + 'px';
            this.colRightDot.style.left = `${rightColX}` + 'px';
            this.colLeftDot.style.top = `${dotY - 4}` + 'px';
            this.colRightDot.style.top = `${dotY - 4}` + 'px';
            this.colLeftDot.style.display = 'block';
            this.colRightDot.style.display = 'block';
            if (cell.classList.contains('e-row-number')) {
                this.colLeftDot.style.display = 'none';
                this.colRightDot.style.display = 'none';
            }
            this.colLeftHit.style.left = `${leftColX - 10}` + 'px';
            this.colRightHit.style.left = `${rightColX - 10}` + 'px';
            this.colLeftHit.style.top = `${dotY - 12}` + 'px';
            this.colRightHit.style.top = `${dotY - 12}` + 'px';
            this.colLeftHit.style.display = 'block';
            this.colRightHit.style.display = 'block';
            if (!isRowNumberCell) {
                const headerCell = this.table.querySelectorAll('thead th:not(.e-row-number)')[colIndex];
                if (headerCell) {
                    const headerRect = headerCell.getBoundingClientRect();
                    const borderValue = Math.round(parseFloat(getComputedStyle(headerCell).borderWidth));
                    this.colActionHandle.style.left = `${(headerRect.left - borderValue) - blockRect.left}` + 'px';
                    this.colActionHandle.style.width = `${headerRect.width + borderValue}` + 'px';
                }
                else {
                    this.colActionHandle.style.left = `${(cellRect.left - borderValue) - blockRect.left}` + 'px';
                    this.colActionHandle.style.width = `${cellRect.width + borderValue}` + 'px';
                }
                this.colActionHandle.dataset.colIndex = colIndex.toString();
                this.colActionHandle.style.display = 'flex';
                const proximity = 6;
                const distanceFromRight = cellRect.right - e.clientX;
                const distanceFromLeft = e.clientX - cellRect.left;
                const isNearColumnRightBoundary = distanceFromRight <= proximity;
                const isNearColumnLeftBoundary = distanceFromLeft <= proximity;
                if (isNearColumnLeftBoundary || isNearColumnRightBoundary) {
                    // Determine which two columns the boundary separates
                    const leftDataIdx = isNearColumnRightBoundary ? colIndex : colIndex - 1;
                    if (leftDataIdx < 0) {
                        // Do not show resize handle for first column
                        this.colResizeHandle.style.display = 'none';
                    }
                    else {
                        const boundary = isNearColumnRightBoundary ? cellRect.right : cellRect.left;
                        const leftOffset = boundary - blockRect.left - 2.5;
                        this.colResizeHandle.style.left = `${leftOffset}px`;
                        this.colResizeHandle.style.top = `${this.table.parentElement.offsetTop}px`;
                        this.colResizeHandle.style.height = `${this.table.offsetHeight}px`;
                        this.colResizeHandle.style.display = 'block';
                        this.colResizeHandle.setAttribute('data-resize-index', String(leftDataIdx));
                    }
                }
            }
            else {
                this.colActionHandle.style.display = 'none';
            }
        });
    }
    // 4) Row insert UI and handlers
    wireRowInsert() {
        const showRowLine = (which) => {
            if (!this.hoveredRow) {
                return;
            }
            const rowRect = this.hoveredRow.getBoundingClientRect();
            const blockRect = this.blockElement.getBoundingClientRect();
            const tbody = this.table.tBodies[0];
            const hasBodyRows = tbody && tbody.rows.length > 0;
            this.rowHoverLine.style.top = `${(which === 'top' ? rowRect.top : rowRect.bottom) - blockRect.top - 2}` + 'px';
            this.rowHoverLine.style.left = `${this.table.parentElement.offsetLeft}` + 'px';
            this.rowHoverLine.style.width = `${this.table.offsetWidth}` + 'px';
            this.rowHoverLine.style.display = 'block';
            const dotTop = which === 'top' ? this.rowTopDot : this.rowBottomDot;
            const dotRect = dotTop.getBoundingClientRect();
            this.rowInsertHandle.style.top = `${dotRect.top - blockRect.top - 7}` + 'px';
            this.rowInsertHandle.style.left = `${dotRect.left - blockRect.left - 7}` + 'px';
            this.rowInsertHandle.style.display = 'flex';
            // In header-only case, keep bottom dot visible
            if (!hasBodyRows && this.hoveredRow.querySelector('th')) {
                // Only bottom dot visible → don't hide it when showing line
                if (which === 'top') {
                    this.rowTopDot.style.visibility = 'hidden'; // top never shown anyway
                }
                else {
                    this.rowBottomDot.style.visibility = ''; // keep visible
                }
            }
            else {
                dotTop.style.visibility = 'hidden';
            }
            const index = Array.from(tbody.rows).indexOf(this.hoveredRow);
            this.rowInsertHandle.dataset.rowIndex = (which === 'top' ? index : index + 1).toString();
        };
        const hideRowLine = () => {
            this.rowHoverLine.style.display = 'none';
            this.rowInsertHandle.style.display = 'none';
            // Restore visibility, but keep bottom dot visible if only header
            const tbody = this.table.tBodies[0];
            const hasBodyRows = tbody && tbody.rows.length > 0;
            if (!hasBodyRows) {
                this.rowTopDot.style.visibility = 'hidden'; // no top dot
                this.rowTopHit.style.visibility = 'hidden';
                this.rowBottomDot.style.visibility = ''; // keep bottom dot visible
            }
            else {
                this.rowTopDot.style.visibility = '';
                this.rowTopHit.style.visibility = '';
                this.rowBottomDot.style.visibility = '';
            }
        };
        this.rowTopHit.addEventListener('mouseenter', (e) => {
            if (this.isFromInsertHandle(e, this.rowInsertHandle)) {
                return;
            }
            showRowLine('top');
        });
        this.rowBottomHit.addEventListener('mouseenter', (e) => {
            if (this.isFromInsertHandle(e, this.rowInsertHandle)) {
                return;
            }
            showRowLine('bottom');
        });
        this.rowInsertHandle.addEventListener('mouseleave', (e) => {
            if (this.isFromInsertHandle(e, this.rowBottomHit) || this.isFromInsertHandle(e, this.rowTopHit)) {
                return;
            }
            hideRowLine();
        });
        this.rowTopHit.addEventListener('mouseleave', (e) => {
            e.stopPropagation();
            if (this.isFromInsertHandle(e, this.rowInsertHandle)) {
                return;
            }
            hideRowLine();
        });
        this.rowBottomHit.addEventListener('mouseleave', (e) => {
            e.stopPropagation();
            if (this.isFromInsertHandle(e, this.rowInsertHandle)) {
                return;
            }
            hideRowLine();
        });
        this.rowInsertHandle.addEventListener('click', (e) => {
            e.preventDefault();
            const index = parseInt(this.rowInsertHandle.dataset.rowIndex, 10);
            this.parent.tableService.addRowAt({
                blockId: this.blockElement.id,
                rowIndex: index
            });
            this.hideRowGripper(); // Hides row pinned bars
            this.hideAllPinnedColBars(); // Hides col pinned bars
            requestAnimationFrame(() => {
                this.hideRowUI();
            });
            this.rowTopHit.style.display = 'none';
            this.rowBottomHit.style.display = 'none';
            this.clearPinnedGripperSelection();
        });
    }
    // 5) Column insert UI and handlers
    wireColInsert() {
        const showColLine = (side) => {
            if (this.hoveredColIndex == null) {
                return;
            }
            const rows = Array.from(this.table.rows);
            const cells = Array.from(rows[0].cells).filter(((cell) => !cell.classList.contains('e-row-number')));
            const anyRowCell = cells[this.hoveredColIndex];
            const blockRect = this.blockElement.getBoundingClientRect();
            const rect = anyRowCell.getBoundingClientRect();
            const boundary = side === 'right' ? rect.right : rect.left;
            this.colHoverLine.style.left = `${boundary - blockRect.left - 2}` + 'px';
            this.colHoverLine.style.top = `${this.table.parentElement.offsetTop}` + 'px';
            this.colHoverLine.style.height = `${this.table.offsetHeight}` + 'px';
            this.colHoverLine.style.display = 'block';
            const dotEl = side === 'left' ? this.colLeftDot : this.colRightDot;
            const dotRect = dotEl.getBoundingClientRect();
            this.colInsertHandle.style.left = `${dotRect.left - blockRect.left - 6}` + 'px';
            this.colInsertHandle.style.top = `${dotRect.top - blockRect.top - 7}` + 'px';
            this.colInsertHandle.style.display = 'flex';
            dotEl.style.visibility = 'hidden';
            this.colInsertHandle.dataset.colIndex = (side === 'right' ? this.hoveredColIndex + 1 : this.hoveredColIndex).toString();
        };
        const hideColLine = () => {
            this.colHoverLine.style.display = 'none';
            this.colInsertHandle.style.display = 'none';
            this.colLeftDot.style.visibility = '';
            this.colRightDot.style.visibility = '';
        };
        this.colLeftHit.addEventListener('mouseenter', (e) => {
            if (this.isFromInsertHandle(e, this.colInsertHandle)) {
                return;
            }
            showColLine('left');
        });
        this.colRightHit.addEventListener('mouseenter', (e) => {
            if (this.isFromInsertHandle(e, this.colInsertHandle)) {
                return;
            }
            showColLine('right');
        });
        this.colInsertHandle.addEventListener('mouseleave', (e) => {
            if (this.isFromInsertHandle(e, this.colLeftHit) || this.isFromInsertHandle(e, this.colRightHit)) {
                return;
            }
            hideColLine();
        });
        this.colLeftHit.addEventListener('mouseleave', (e) => {
            e.stopPropagation();
            if (this.isFromInsertHandle(e, this.colInsertHandle)) {
                return;
            }
            hideColLine();
        });
        this.colRightHit.addEventListener('mouseleave', (e) => {
            e.stopPropagation();
            if (this.isFromInsertHandle(e, this.colInsertHandle)) {
                return;
            }
            hideColLine();
        });
        this.colInsertHandle.addEventListener('click', (e) => {
            e.preventDefault();
            const index = parseInt(this.colInsertHandle.dataset.colIndex, 10);
            this.parent.tableService.addColumnAt({
                blockId: this.blockElement.id,
                colIndex: index
            });
            this.hideRowGripper(); // Hides row pinned bars
            this.hideAllPinnedColBars(); // Hides col pinned bars
            requestAnimationFrame(() => {
                this.hideColUI();
            });
            this.colLeftHit.style.display = 'none';
            this.colRightHit.style.display = 'none';
            this.clearPinnedGripperSelection();
        });
    }
    isFromInsertHandle(e, insertHandle) {
        const relatedTarget = e.relatedTarget;
        return (relatedTarget !== null && insertHandle !== null && (relatedTarget === insertHandle));
    }
    /**
     * Displays a row gripper for the specified DOM row index.
     * Handles row selection, focus, pinned bar creation, and optional gripper popup.
     * Updates DOM elements, dataset attributes, and integrates with undo/redo tracking.
     *
     * @param {number} domRowIdx - The DOM/visual row index to show the gripper for.
     * @param {boolean} [isFirstRow=false] - Indicates if the row is the first row, triggering popup options.
     * @returns {void}
     *
     * @hidden
     */
    showRowGripperForDomRow(domRowIdx, isFirstRow = false) {
        const rowEl = this.table.rows[parseInt(domRowIdx.toString(), 10)];
        const cellBlock = rowEl.querySelector('.e-block');
        const props = this.blockModel.properties;
        // No gripper or popup for header
        if (props.enableHeader && domRowIdx === 0) {
            return;
        }
        const rowRect = rowEl.getBoundingClientRect();
        const blockRect = this.blockElement.getBoundingClientRect();
        this.addRowSelection(this.table, domRowIdx);
        this.focusCellsInRows([domRowIdx]);
        // Focus first cell in the row
        setCursorPosition(getBlockContentElement(cellBlock), 0);
        this.parent.setFocusToBlock(cellBlock);
        // Create and position a new pinned bar for this row
        const rowPinned = this.createPinnedRowBar();
        rowPinned.style.top = `${Math.round(rowRect.top - blockRect.top - 1)}px`;
        rowPinned.style.height = `${rowRect.height + 1}px`;
        rowPinned.classList.add('e-action-bar-active');
        rowPinned.style.display = 'flex';
        rowPinned.dataset.rowIndex = domRowIdx.toString();
        this.blockElement.appendChild(rowPinned);
        this.rowPinned = rowPinned;
        rowPinned.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const clickedDomRowIdx = parseInt(rowPinned.dataset.rowIndex, 10);
            // Clear all selections and grippers
            this.resetAllTableSelectionUI();
            // Re-show gripper + popup for the clicked row
            this.showRowGripperForDomRow(clickedDomRowIdx, true);
            // Refocus the clicked row (optional but good UX)
            const clickedRowEl = this.table.rows[parseInt(clickedDomRowIdx.toString(), 10)];
            const cellBlock = clickedRowEl.querySelector('.e-block');
            if (cellBlock) {
                setCursorPosition(getBlockContentElement(cellBlock), 0);
                this.parent.setFocusToBlock(cellBlock);
            }
        });
        this.rowActionHandle.style.display = 'none';
        this.colPinned.style.display = 'none';
        this.colPinned.classList.remove('e-action-bar-active');
        if (isFirstRow) {
            this.showGripperPopup(this.rowActionHandle, rowPinned, this.blockModel, {
                label: 'Delete Row',
                iconCss: 'e-trash',
                callback: () => { this.deleteSelectedRows(domRowIdx); }
            });
        }
    }
    deleteSelectedRows(domRowIdx) {
        const selectedRows = this.table.querySelectorAll('tr.e-row-selected');
        const selectedCount = selectedRows.length;
        const props = this.blockModel.properties;
        if (selectedCount === 0) {
            return; // nothing selected → no-op, no undo entry
        }
        if (selectedCount === 1) {
            const modelIdx = toModelRow(domRowIdx, props.enableHeader);
            this.parent.tableService.deleteRowAt({
                blockId: this.blockId,
                modelIndex: modelIdx
            });
        }
        else {
            const rowsMeta = [];
            // Collect metadata for undo/redo
            Array.from(selectedRows)
                .map((row) => parseInt(row.dataset.row, 10))
                .sort((a, b) => b - a)
                .forEach((domRowIdx) => {
                const modelIdx = toModelRow(domRowIdx, props.enableHeader);
                const rowModel = props.rows[parseInt(modelIdx.toString(), 10)];
                if (rowModel) {
                    rowsMeta.push({
                        index: modelIdx,
                        rowModel: decoupleReference(rowModel)
                    });
                }
            });
            // Track bulk undo/redo **once**
            this.parent.undoRedoAction.trackBulkRowDeletionForUndoRedo({
                blockId: this.blockId,
                rows: rowsMeta
            });
            // Perform deletions (skip internal single tracking)
            rowsMeta.forEach(({ index }) => {
                this.parent.tableService.deleteRowAt({
                    blockId: this.blockId,
                    modelIndex: index,
                    isUndoRedoAction: true
                });
            });
        }
    }
    /**
     * Displays a column gripper for the specified DOM column index.
     * Handles column selection, focus, pinned bar creation, and optional gripper popup.
     * Updates DOM elements, dataset attributes, and integrates with undo/redo tracking.
     *
     * @param {number} domColIdx - The DOM/visual column index to show the gripper for.
     * @param {boolean} [isFirstCol=false] - Indicates if the column is the first column, triggering popup options.
     * @returns {void}
     *
     * @hidden
     */
    showColGripperForDomCol(domColIdx, isFirstCol = false) {
        const props = this.blockModel.properties;
        // 1. Get top cell for popup positioning (header if exists, else first body)
        let topCell;
        if (props.enableHeader) {
            const headerCells = this.table.querySelectorAll('thead th:not(.e-row-number)');
            topCell = headerCells[parseInt(domColIdx.toString(), 10)];
        }
        else {
            topCell = getDataCell(this.table, 0, domColIdx);
        }
        // 2. Get target cell for focus + pinned bar positioning (body cell, or header if no body logic needed)
        const headerCell = props.enableHeader
            ? this.table.querySelectorAll('thead th:not(.e-row-number)')[parseInt(domColIdx.toString(), 10)]
            : null;
        const targetCell = getDataCell(this.table, (headerCell ? 1 : 0), domColIdx);
        const cellBlock = targetCell.querySelector('.e-block');
        const cellRect = targetCell.getBoundingClientRect();
        const blockRect = this.blockElement.getBoundingClientRect();
        // Add column selection without clearing others
        this.addColumnSelection(this.table, domColIdx);
        // add e-cell-focus class to selected col cells
        this.focusCellsInColumns([domColIdx]);
        // Focus
        setCursorPosition(getBlockContentElement(cellBlock), 0);
        this.parent.setFocusToBlock(cellBlock);
        let colPinnedLeftOffset = cellRect.left - blockRect.left;
        let colPinnedWidth = cellRect.width;
        if (headerCell) {
            const headerRect = headerCell.getBoundingClientRect();
            const headerBorderValue = Math.round(parseFloat(getComputedStyle(headerCell).borderWidth));
            colPinnedLeftOffset = (headerRect.left - headerBorderValue) - blockRect.left;
            colPinnedWidth = headerRect.width + headerBorderValue;
        }
        // Create and position pinned bar
        const colPinned = this.createPinnedColBar();
        colPinned.style.left = `${colPinnedLeftOffset}px`;
        colPinned.style.width = `${colPinnedWidth}px`;
        colPinned.classList.add('e-action-bar-active');
        colPinned.style.display = 'flex';
        colPinned.dataset.colIndex = domColIdx.toString();
        this.blockElement.appendChild(colPinned);
        this.colPinned = colPinned;
        colPinned.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const clickedDomColIdx = parseInt(colPinned.dataset.colIndex, 10);
            // Focus only this column (clear others + re-apply this one)
            this.focusSinglePinnedColumn(clickedDomColIdx);
        });
        // Hide regular handles
        this.colActionHandle.style.display = 'none';
        if (isFirstCol) {
            this.showGripperPopup(this.colActionHandle, colPinned, this.blockModel, {
                label: 'Delete Column',
                iconCss: 'e-trash',
                callback: () => { this.deleteSelectedColumns(); }
            });
        }
    }
    deleteSelectedColumns() {
        const props = this.blockModel.properties;
        const selectedCells = this.blockElement.querySelectorAll('td.e-col-selected, th.e-col-selected');
        const uniqueColIndices = new Set();
        selectedCells.forEach((cell) => {
            const colIdx = parseInt(cell.dataset.col || '-1', 10);
            if (colIdx >= 0) {
                uniqueColIndices.add(colIdx);
            }
        });
        const selectedCount = uniqueColIndices.size;
        const totalDataColumns = props.columns.length;
        if (selectedCount === totalDataColumns && totalDataColumns > 0) {
            // Full table columns selected → delete entire block directly
            const block = getBlockModelById(this.blockId, this.parent.getEditorBlocks());
            const blockElement = this.parent.getBlockElementById(this.blockId);
            const adjacentBlock = (blockElement.nextElementSibling || blockElement.previousElementSibling);
            if (adjacentBlock) {
                this.parent.setFocusAndUIForNewBlock(adjacentBlock);
            }
            if (block && blockElement) {
                this.parent.lastHighlightedBlockId = this.blockId;
                this.parent.execCommand({
                    command: 'DeleteBlock',
                    state: {
                        blockElement,
                        isUndoRedoAction: false,
                        preventMinimumOne: true,
                        preventEventTrigger: false
                    }
                });
            }
            return;
        }
        const sortedIndices = Array.from(uniqueColIndices).sort((a, b) => b - a);
        if (sortedIndices.length > 1) {
            const colsMeta = [];
            sortedIndices.forEach((dataColIdx) => {
                const colModel = props.columns[parseInt(dataColIdx.toString(), 10)];
                const columnCells = props.rows.map((r) => {
                    const cell = r.cells[parseInt(dataColIdx.toString(), 10)];
                    return decoupleReference(cell);
                });
                colsMeta.push({
                    index: dataColIdx,
                    columnModel: decoupleReference(colModel),
                    columnCells
                });
            });
            this.parent.undoRedoAction.trackBulkColumnDeletionForUndoRedo({
                blockId: this.blockId,
                cols: colsMeta
            });
            sortedIndices.forEach((dataColIdx) => {
                this.parent.tableService.deleteColumnAt({
                    blockId: this.blockId,
                    colIndex: dataColIdx,
                    isUndoRedoAction: true
                });
            });
        }
        else if (sortedIndices.length === 1) {
            // Single column — normal deletion
            this.parent.tableService.deleteColumnAt({
                blockId: this.blockId,
                colIndex: sortedIndices[0]
            });
        }
    }
    focusSinglePinnedColumn(domColIdx) {
        // Clear everything first
        this.resetAllTableSelectionUI();
        // Re-select only the clicked column
        this.addColumnSelection(this.table, domColIdx);
        this.focusCellsInColumns([domColIdx]);
        // Re-show gripper + popup for the clicked column
        this.showColGripperForDomCol(domColIdx, true);
        // Refocus the first body cell in that column (or header if no body)
        const props = this.blockModel.properties;
        const startRow = props.enableHeader ? 1 : 0; // skip header if present
        const rowEl = this.table.tBodies[0].rows[parseInt(startRow.toString(), 10)];
        const domCol = toDomCol(domColIdx, props.enableRowNumbers);
        const cell = rowEl.cells[parseInt(domCol.toString(), 10)];
        this.parent.tableService.removeCellFocus(this.table);
        this.parent.tableService.addCellFocus(cell, true);
        const cellBlock = cell.querySelector('.e-block');
        setCursorPosition(getBlockContentElement(cellBlock), 0);
        this.parent.setFocusToBlock(cellBlock);
    }
    /**
     * Adds a selection state to the specified row in the table.
     * Does not clear previous selections, allowing multiple rows to be selected.
     *
     * @param {HTMLTableElement} table - The table element containing the row.
     * @param {number} rowIndex - The index of the row to mark as selected.
     * @returns {void}
     *
     * @hidden
     */
    addRowSelection(table, rowIndex) {
        // Add selection without removing previous
        if (rowIndex == null || rowIndex < 0) {
            return;
        }
        const row = table.rows[rowIndex];
        if (row) {
            row.classList.add('e-row-selected');
        }
    }
    createPinnedRowBar() {
        const pinned = createElement('div', { className: 'e-row-action-handle e-pinned e-action-handle', attrs: { contenteditable: 'false', 'data-icon-type': 'row' } });
        const pinnedIcon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        pinned.appendChild(pinnedIcon);
        return pinned;
    }
    /**
     * Hides all pinned row grippers from the table block element.
     * Removes pinned row action handles from the DOM and restores the default row action handle display.
     *
     * @returns {void}
     *
     * @hidden
     */
    hideRowGripper() {
        const allPinnedRows = this.blockElement.querySelectorAll('.e-row-action-handle.e-pinned');
        allPinnedRows.forEach((pinned) => {
            pinned.parentElement.removeChild(pinned);
        });
        if (this.rowActionHandle) {
            this.rowActionHandle.style.display = 'none';
        }
    }
    // 6) Row/Col action handle wiring
    wireActionHandles() {
        // Row action
        this.rowActionHandle.addEventListener('click', (e) => {
            if (this.rowInsertHandle.style.display !== 'none') {
                return;
            }
            e.preventDefault();
            const domRowIdx = parseInt(this.rowActionHandle.dataset.rowIndex, 10);
            if (e.shiftKey) {
                let start;
                let end;
                // Case 1: Anchor already set from previous gripper click
                if (this.lastRowAnchorIndex !== null) {
                    start = Math.min(this.lastRowAnchorIndex, domRowIdx);
                    end = Math.max(this.lastRowAnchorIndex, domRowIdx);
                }
                // Case 2: No anchor → fall back to currently selected rows (mouse drag / Shift+Arrow)
                else {
                    const selectedRows = this.table.querySelectorAll('tr.e-row-selected');
                    if (selectedRows.length === 0) {
                        // No existing selection → treat as normal single click
                        start = end = domRowIdx;
                    }
                    else {
                        const indices = Array.from(selectedRows).map((tr) => parseInt(tr.dataset.row, 10));
                        start = Math.min(...indices, domRowIdx);
                        end = Math.max(...indices, domRowIdx);
                    }
                }
                this.removeRowColSelection(this.table);
                this.hideRowGripper();
                this.hideAllPinnedColBars();
                this.parent.tableService.removeCellFocus(this.table);
                let isFirst = true;
                for (let rowIndex = start; rowIndex <= end; rowIndex++) {
                    if (!this.hasPinnedRowGripper(rowIndex)) {
                        this.showRowGripperForDomRow(rowIndex, isFirst);
                        isFirst = false;
                    }
                }
            }
            else {
                // Normal single click (unchanged)
                this.resetAllTableSelectionUI();
                this.showRowGripperForDomRow(domRowIdx, true);
                // Remember this row as anchor for next Shift+click
                this.lastRowAnchorIndex = domRowIdx;
            }
        });
        // Column action
        this.colActionHandle.addEventListener('mousedown', (e) => e.preventDefault());
        this.colActionHandle.addEventListener('click', (e) => {
            if (this.colInsertHandle.style.display !== 'none') {
                return;
            }
            e.preventDefault();
            const domColIdx = parseInt(this.colActionHandle.dataset.colIndex, 10);
            if (e.shiftKey) {
                let start;
                let end;
                // Case 1: Anchor already set from previous gripper click
                if (this.lastColAnchorIndex !== null) {
                    start = Math.min(this.lastColAnchorIndex, domColIdx);
                    end = Math.max(this.lastColAnchorIndex, domColIdx);
                }
                // Case 2: No anchor → fall back to currently selected columns (mouse drag / Shift+Arrow)
                else {
                    const selectedColCells = this.table.querySelectorAll('td.e-col-selected, th.e-col-selected');
                    if (selectedColCells.length === 0) {
                        // No existing selection → treat as normal single click
                        start = end = domColIdx;
                    }
                    else {
                        const indices = [];
                        const colMap = new Set(); // to deduplicate
                        Array.from(selectedColCells).forEach((cell) => {
                            const col = parseInt(cell.dataset.col, 10);
                            if (!colMap.has(col)) {
                                colMap.add(col);
                                indices.push(col);
                            }
                        });
                        start = Math.min(...indices, domColIdx);
                        end = Math.max(...indices, domColIdx);
                    }
                }
                this.resetAllTableSelectionUI();
                let isFirst = true;
                for (let colIndex = start; colIndex <= end; colIndex++) {
                    if (!this.hasPinnedColGripper(colIndex)) {
                        this.showColGripperForDomCol(colIndex, isFirst);
                        isFirst = false;
                    }
                }
            }
            else {
                // Normal single click (unchanged)
                this.resetAllTableSelectionUI();
                this.showColGripperForDomCol(domColIdx, true);
                // Remember this column as anchor for next Shift+click
                this.lastColAnchorIndex = domColIdx;
            }
        });
    }
    hasPinnedColGripper(colIndex) {
        return !!this.blockElement.querySelector(`.e-col-action-handle.e-pinned[data-col-index="${colIndex}"]`);
    }
    hasPinnedRowGripper(rowIndex) {
        return !!this.blockElement.querySelector(`.e-row-action-handle.e-pinned[data-row-index="${rowIndex}"]`);
    }
    /**
     * Adds a selection state to the specified column in the table.
     * Does not clear previous selections, allowing multiple columns to be selected.
     *
     * @param {HTMLTableElement} table - The table element containing the column.
     * @param {number} colIndex - The index of the column to mark as selected.
     * @returns {void}
     *
     * @hidden
     */
    addColumnSelection(table, colIndex) {
        if (colIndex == null || colIndex < 0) {
            return;
        }
        Array.from(table.rows).forEach((r) => {
            const cells = Array.from(r.cells).filter((cell) => !cell.classList.contains('e-row-number'));
            if (cells[parseInt(colIndex.toString(), 10)]) {
                cells[parseInt(colIndex.toString(), 10)].classList.add('e-col-selected');
            }
        });
    }
    createPinnedColBar() {
        const pinned = createElement('div', {
            className: 'e-col-action-handle e-pinned e-action-handle',
            attrs: { contenteditable: 'false', 'data-icon-type': 'col' }
        });
        const icon = createElement('span', { className: 'e-icons e-block-drag-icon' });
        icon.style.transform = 'rotate(90deg)';
        pinned.appendChild(icon);
        return pinned;
    }
    /**
     * Removes all pinned column bars from the table block element.
     * Queries for pinned column action handles and deletes them from the DOM.
     *
     * @returns {void}
     *
     * @hidden
     */
    hideAllPinnedColBars() {
        const allPinnedCols = this.blockElement.querySelectorAll('.e-col-action-handle.e-pinned');
        allPinnedCols.forEach((el) => el.remove());
    }
    // 7) Table focus, mouseleave, and outside-click cleanup
    wireFocusAndCleanup() {
        this.table.addEventListener('focusin', () => {
            this.removeRowColSelection(this.table);
            this.rowPinned.style.display = 'none';
            this.colPinned.style.display = 'none';
        });
        this.blockElement.addEventListener('mouseleave', () => {
            this.hideRowUI();
            this.hideColUI();
            this.hideHitZones();
            this.rowTopDot.style.visibility = '';
            this.rowBottomDot.style.visibility = '';
            this.colLeftDot.style.visibility = '';
            this.colRightDot.style.visibility = '';
            this.colResizeHandle.style.display = 'none';
        });
        this.table.addEventListener('mousedown', (e) => {
            const target = e.target;
            const cell = target.closest('td, th');
            if (!cell || (cell && cell.classList.contains('e-row-number'))) {
                return;
            }
            if (cell.classList.contains('e-cell-focus')) {
                const selectedCells = getSelectedCells(this.blockElement);
                if (selectedCells && selectedCells.length === 1) {
                    return;
                }
            }
            this.parent.tableService.removeCellFocus(this.table);
            this.parent.tableService.addCellFocus(cell, true);
            this.hideRowGripper();
            this.hideAllPinnedColBars();
        });
        const onDocClick = (e) => {
            const t = e.target;
            const inside = !!t.closest(`table.e-table-element[data-block-id="${this.blockId}"]`);
            const blkEl = t.closest('.e-table-block');
            const tableSlashItem = t.closest('.e-list-item[data-value="Table"]');
            if (inside || (blkEl && blkEl.contains(t)) || tableSlashItem) {
                return;
            }
            this.removeRowColSelection(this.table);
            document.querySelectorAll('.e-row-action-handle.e-pinned').forEach((node) => (node.style.display = 'none'));
            document.querySelectorAll('.e-col-action-handle.e-pinned').forEach((node) => (node.style.display = 'none'));
            if (this.parent.tableService) {
                this.parent.tableService.removeCellFocus(this.table);
            }
        };
        document.addEventListener('click', onDocClick);
    }
    // 8) Observers and sync helpers
    wireObservers() {
        const RO = window.ResizeObserver;
        if (RO) {
            const ro = new RO(() => { this.syncRowUI(); });
            ro.observe(this.table);
            Array.from(this.table.rows).forEach((r) => ro.observe(r));
            Array.from(this.table.querySelectorAll('th')).forEach((th) => ro.observe(th));
        }
        this.parent.observer.on('handleEscapeKey', this.handleEscapeAction, this);
    }
    // 9) Column resize wiring
    wireColResize() {
        let startMouseX = 0;
        let initialColumnWidthPx = 0;
        let initialHoverLineLeft = 0;
        let resizingColumnIndex = -1; // data index of the column being resized
        let tableRect;
        let blockRect;
        // Clamp width and compute hover line position
        const computeResizedValues = (deltaX) => {
            const unclampedWidth = initialColumnWidthPx + deltaX;
            const newWidthPx = Math.max(constants.TABLE_COL_MIN_WIDTH, unclampedWidth);
            return newWidthPx;
        };
        const onMouseMove = (e) => {
            if (!this.isResizing) {
                return;
            }
            e.preventDefault();
            this.hideColUI();
            const deltaX = (e.clientX + this.tableContainer.scrollLeft) - startMouseX;
            const props = this.blockModel.properties;
            const colgroup = this.table.querySelector('colgroup');
            const domColIndex = toDomCol(resizingColumnIndex, props.enableRowNumbers);
            const newWidthPx = computeResizedValues(deltaX);
            // Update left column width (Only DOM here)
            const leftColEl = colgroup.children[domColIndex];
            leftColEl.style.width = `${newWidthPx.toFixed(0)}px`;
            // Update hover line
            this.colHoverLine.style.display = 'block';
            this.colHoverLine.style.left = `${leftColEl.getBoundingClientRect().right - blockRect.left - 2}px`;
            this.colHoverLine.style.height = this.table.getBoundingClientRect().height - 2 + 'px';
            this.colResizeHandle.style.left = `${leftColEl.getBoundingClientRect().right - blockRect.left - 2}px`;
            this.colHoverLine.style.display = 'block';
            // Shrink table width when dragging left
            if (deltaX < 0) {
                const currentTableWidth = this.table.offsetWidth;
                this.table.style.width = `${currentTableWidth + deltaX}px`;
            }
        };
        const onMouseUp = (e) => {
            if (!this.isResizing) {
                return;
            }
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            const deltaX = (e.clientX + this.tableContainer.scrollLeft) - startMouseX;
            if (deltaX === 0) {
                this.colHoverLine.style.display = 'none';
                setResizeStatus(false);
                return;
            }
            const newWidthPx = computeResizedValues(deltaX);
            const props = this.blockModel.properties;
            const oldBlock = decoupleReference(getBlockModelById(this.blockId, this.parent.getEditorBlocks()));
            // Final update
            const colgroup = this.table.querySelector('colgroup');
            const domColIndex = toDomCol(resizingColumnIndex, props.enableRowNumbers);
            const leftColEl = colgroup.children[domColIndex];
            leftColEl.style.width = `${newWidthPx.toFixed(0)}px`;
            props.columns[resizingColumnIndex].width = `${newWidthPx.toFixed(0)}px`;
            this.colHoverLine.style.display = 'none';
            setResizeStatus(false);
            // Notify changes and track
            this.parent.tableService.triggerBlockUpdate(this.blockModel, oldBlock);
            this.parent.undoRedoAction.trackTableColumnResizeForUndoRedo({
                blockId: this.blockId,
                resizedColIndex: resizingColumnIndex,
                oldWidthValue: initialColumnWidthPx,
                newWidthValue: newWidthPx
            });
        };
        // Start resize
        this.colResizeHandle.addEventListener('mousedown', (e) => {
            const props = this.blockModel.properties;
            if (props.readOnly) {
                return;
            }
            e.preventDefault();
            this.clearPinnedGripperSelection();
            this.hideColUI();
            this.hideRowUI();
            this.parent.tableService.removeCellFocus(this.table);
            const colIndexAttr = this.colResizeHandle.getAttribute('data-resize-index');
            if (!colIndexAttr) {
                return;
            }
            resizingColumnIndex = parseInt(colIndexAttr, 10);
            // Switch to px mode if not already
            if (this.table.getAttribute('data-col-width-mode') !== 'px') {
                this.convertColgroupToPxMode();
            }
            tableRect = this.table.getBoundingClientRect();
            blockRect = this.blockElement.getBoundingClientRect();
            const colgroup = this.table.querySelector('colgroup');
            const domColIndex = toDomCol(resizingColumnIndex, props.enableRowNumbers);
            const columnEl = colgroup.children[domColIndex];
            initialColumnWidthPx = columnEl.getBoundingClientRect().width;
            startMouseX = e.clientX + this.tableContainer.scrollLeft;
            initialHoverLineLeft = columnEl.getBoundingClientRect().right - blockRect.left - 2;
            // Show hover line
            this.colHoverLine.style.top = `${tableRect.top - blockRect.top}px`;
            this.colHoverLine.style.height = `${tableRect.height - 2}px`;
            this.colHoverLine.style.left = `${initialHoverLineLeft}px`;
            this.colHoverLine.style.display = 'block';
            this.colResizeHandle.style.left = `${initialHoverLineLeft}px`;
            this.colHoverLine.style.display = 'block';
            document.body.style.userSelect = 'none';
            setResizeStatus(true);
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
        // Hide UI elements on horizontal scroll
        this.table.parentElement.addEventListener('scroll', () => {
            this.hideColUI();
            this.hideRowUI();
            this.hideHitZones();
            this.clearPinnedGripperSelection();
        });
        const setResizeStatus = (value) => {
            this.isResizing = value;
            this.table.setAttribute('data-resizing', value ? 'true' : 'false');
        };
    }
    convertColgroupToPxMode() {
        const props = this.blockModel.properties;
        const colgroup = this.table.querySelector('colgroup');
        props.columns.forEach((col, i) => {
            const domIdx = toDomCol(i, props.enableRowNumbers);
            const colEl = colgroup.children[domIdx];
            const currentWidthPx = colEl.getBoundingClientRect().width;
            const pxWidth = currentWidthPx.toFixed(0);
            colEl.style.width = `${pxWidth}px`;
            col.width = `${pxWidth}px`;
        });
        setTableWidthMode(this.table, 'px');
        this.parent.observer.on('handleEscapeKey', this.handleEscapeAction, this);
    }
    syncRowUI() {
        if (!this.hoveredRow || (this.table && this.table.querySelectorAll('tr.e-row-selected').length > 1)) {
            return;
        }
        const rowRect = this.hoveredRow.getBoundingClientRect();
        const blockRect = this.blockElement.getBoundingClientRect();
        const xLeft = this.table.offsetLeft - 8;
        this.rowTopDot.style.left = `${xLeft - 4}` + 'px';
        this.rowBottomDot.style.left = `${xLeft - 4}` + 'px';
        this.rowTopDot.style.top = `${rowRect.top - blockRect.top - 3}` + 'px';
        this.rowBottomDot.style.top = `${rowRect.bottom - blockRect.top - 3}` + 'px';
        if (this.rowActionHandle.style.display !== 'none') {
            this.rowActionHandle.style.top = `${rowRect.top - blockRect.top}` + 'px';
            this.rowActionHandle.style.height = `${rowRect.height}` + 'px';
        }
        const pinnedRow = this.blockElement.querySelector('.e-row-action-handle.e-pinned');
        if (pinnedRow && pinnedRow.style.display !== 'none') {
            pinnedRow.style.top = `${rowRect.top - blockRect.top}` + 'px';
            pinnedRow.style.height = `${rowRect.height}` + 'px';
        }
    }
    hideRowUI() {
        this.rowInsertHandle.style.display = 'none';
        this.rowHoverLine.style.display = 'none';
        this.rowActionHandle.style.display = 'none';
        this.rowTopDot.style.display = 'none';
        this.rowBottomDot.style.display = 'none';
    }
    hideColUI() {
        this.colInsertHandle.style.display = 'none';
        this.colHoverLine.style.display = 'none';
        this.colActionHandle.style.display = 'none';
        this.colLeftDot.style.display = 'none';
        this.colRightDot.style.display = 'none';
    }
    hideHitZones() {
        [this.rowTopHit, this.rowBottomHit, this.colLeftHit, this.colRightHit].forEach((el) => el.style.display = 'none');
    }
    /**
     * Removes all row and column selection states from the given table.
     * Clears the `e-row-selected` class from rows and the `e-col-selected` class from cells.
     *
     * @param {HTMLTableElement} table - The table element to clear selections from.
     * @returns {void}
     *
     * @hidden
     */
    removeRowColSelection(table) {
        Array.from(table.rows).forEach((r) => r.classList.remove('e-row-selected'));
        Array.from(table.querySelectorAll('td, th')).forEach((c) => {
            const cell = c;
            cell.classList.remove('e-col-selected');
        });
        this.lastRowAnchorIndex = null;
        this.lastColAnchorIndex = null;
    }
    clearPinnedGripperSelection() {
        // Clear selections
        this.removeRowColSelection(this.table);
        // hide pinned handles
        this.rowPinned.style.display = 'none';
        this.rowPinned.classList.remove('e-action-bar-active');
        this.colPinned.style.display = 'none';
        this.colPinned.classList.remove('e-action-bar-active');
        this.lastRowAnchorIndex = null;
        this.lastColAnchorIndex = null;
    }
    showGripperPopup(actionHandle, pinnedActionHandle, blockModel, action) {
        const existingPopup = this.parent.rootEditorElement.querySelector('.e-table-gripper-action-popup');
        if (existingPopup && existingPopup.parentElement) {
            existingPopup.parentElement.removeChild(existingPopup);
        }
        const popup = createElement('div', {
            className: 'e-table-gripper-action-popup'
        });
        this.parent.rootEditorElement.appendChild(popup);
        const item = createElement('div', {
            className: `e-table-gripper-action-item e-icons ${action.iconCss}`
        });
        item.setAttribute('title', 'Delete');
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            try {
                action.callback();
            }
            finally {
                // Cleanup
                if (this.popupObj && this.parent && this.parent.popupRenderer) {
                    this.popupObj.hide();
                    this.parent.popupRenderer.destroyPopup(this.popupObj);
                    this.popupObj = null;
                }
                actionHandle.style.display = 'none';
                pinnedActionHandle.style.display = 'none';
                this.hideRowGripper();
                this.hideAllPinnedColBars();
            }
        });
        popup.appendChild(item);
        const iconType = actionHandle.getAttribute('data-icon-type');
        const args = {
            element: popup,
            content: item,
            width: 'auto',
            height: 'auto',
            relateTo: actionHandle,
            position: { X: 'left', Y: 'top' },
            offsetX: iconType === 'col' ? 20 : -11,
            offsetY: iconType === 'col' ? -8 : 21
        };
        this.popupObj = this.parent.popupRenderer.renderPopup(args);
        this.popupObj.show();
        this.parent.popupRenderer.adjustPopupPositionRelativeToTarget(pinnedActionHandle, this.popupObj);
        const removePopup = (e) => {
            const target = e.target;
            // Find the current popup from DOM (not from closure)
            const currentPopup = this.parent.rootEditorElement.querySelector('.e-table-gripper-action-popup');
            if (currentPopup && !currentPopup.contains(target) && !target.closest('.e-action-handle')) {
                this.handleRemovePopup();
                document.removeEventListener('mousedown', removePopup);
            }
        };
        document.addEventListener('mousedown', removePopup);
    }
    /**
     * Handles removal of the active popup instance.
     * Hides the popup, destroys it via the parent renderer, and clears the reference.
     *
     * @returns {void}
     *
     * @hidden
     */
    handleRemovePopup() {
        if (this.popupObj && this.parent && this.parent.popupRenderer) {
            this.popupObj.hide();
            this.parent.popupRenderer.destroyPopup(this.popupObj);
            this.popupObj = null;
        }
    }
    handleEscapeAction() {
        this.handleRemovePopup();
        const isColumnGripper = this.colPinned.classList.contains('e-action-bar-active');
        const isRowGripper = this.rowPinned.classList.contains('e-action-bar-active');
        if (isColumnGripper) {
            const focusCell = this.table.querySelector('thead th.e-col-selected');
            if (focusCell) {
                this.parent.tableService.addCellFocus(focusCell, true);
            }
        }
        else if (isRowGripper) {
            const selectedRow = this.table.querySelector('.e-row-selected');
            if (selectedRow) {
                const focusCell = selectedRow.querySelector('td:not(.e-row-number)');
                this.parent.tableService.addCellFocus(focusCell, true);
            }
        }
        this.clearPinnedGripperSelection();
    }
    focusCellsInRows(rowIndices) {
        rowIndices.forEach((rowIndex) => {
            const row = this.table.rows[parseInt(rowIndex.toString(), 10)];
            if (!row) {
                return;
            }
            Array.from(row.cells).forEach((cell) => {
                if (!cell.classList.contains('e-row-number')) {
                    cell.classList.add(constants.TABLE_CELL_FOCUS);
                }
            });
        });
    }
    focusCellsInColumns(colIndices) {
        const props = this.blockModel.properties;
        const enableRowNumbers = props.enableRowNumbers;
        colIndices.forEach((colIndex) => {
            const domCol = toDomCol(colIndex, enableRowNumbers);
            for (let r = 0; r < this.table.rows.length; r++) {
                const cell = this.table.rows[parseInt(r.toString(), 10)].cells[parseInt(domCol.toString(), 10)];
                if (cell && !cell.classList.contains('e-row-number')) {
                    cell.classList.add(constants.TABLE_CELL_FOCUS);
                }
            }
        });
    }
    /**
     * Resets all row/column selection UI states: clears visual selection classes,
     * hides floating and pinned grippers (both row and column), and removes cell focus.
     * @returns {void}
     *
     * @hidden
     */
    resetAllTableSelectionUI() {
        this.removeRowColSelection(this.table); // clears .e-row-selected / .e-col-selected
        this.hideRowGripper(); // hides floating row gripper
        this.hideAllPinnedColBars(); // hides floating column grippers
        this.parent.tableService.removeCellFocus(this.table); // removes .e-cell-focus from all cells
    }
    destroy() {
        this.rowInsertHandle = null;
        this.rowActionHandle = null;
        this.rowHoverLine = null;
        this.rowTopDot = null;
        this.rowBottomDot = null;
        this.colInsertHandle = null;
        this.colActionHandle = null;
        this.colHoverLine = null;
        this.colLeftDot = null;
        this.colRightDot = null;
        this.rowPinned = null;
        this.colPinned = null;
        this.rowTopHit = null;
        this.rowBottomHit = null;
        this.colLeftHit = null;
        this.colRightHit = null;
        // State
        this.hoveredRow = null;
        this.hoveredColIndex = null;
        this.isMultiSelecting = null;
        // Instance
        if (this.popupObj) {
            this.parent.popupRenderer.destroyPopup(this.popupObj);
            this.popupObj = null;
        }
        this.parent.observer.off('handleEscapeKey', this.handleEscapeAction);
    }
}
